import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { CloseOutlined, DesktopWindowsOutlined, TabletOutlined, PhoneIphoneOutlined } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { PRIMARY } from '../constants';

export function InspectorSection({ label, children }) {
  return (
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: '0.68rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

export function StyleRow({ label, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
      <Typography sx={{ fontSize: '0.7rem', color: '#7a8494', width: 48, flexShrink: 0 }}>{label}</Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

export function StyleInput({ value, onChange, type = 'text', placeholder }) {
  const [local, setLocal] = useState(value ?? '');
  React.useEffect(() => { setLocal(value ?? ''); }, [value]);
  return (
    <input
      type={type}
      value={local}
      onChange={e => setLocal(e.target.value)}
      onBlur={() => { if (local !== (value ?? '')) onChange(local); }}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
      placeholder={placeholder}
      style={{ width: '100%', border: '1px solid #e4e9f0', borderRadius: 6, padding: '4px 8px', fontSize: '12px', outline: 'none', fontFamily: 'inherit', background: '#f8f9fb', boxSizing: 'border-box' }}
      onFocus={e => e.target.style.borderColor = PRIMARY}
    />
  );
}

export function StyleSelect({ value, onChange, options }) {
  return (
    <select
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      style={{ width: '100%', border: '1px solid #e4e9f0', borderRadius: 6, padding: '4px 8px', fontSize: '12px', outline: 'none', fontFamily: 'inherit', background: '#f8f9fb', cursor: 'pointer', boxSizing: 'border-box' }}
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// UnitInput — numeric input with a unit dropdown (px / % / rem / em).
export function UnitInput({ value, onChange, placeholder }) {
  const UNITS = ['px', '%', 'rem', 'em'];

  const parseValue = (v) => {
    if (!v && v !== 0) return { num: '', unit: 'px' };
    const str = v.toString().trim();
    for (const u of ['rem', 'em', 'px', '%']) {
      if (str.endsWith(u)) return { num: str.slice(0, -u.length).trim(), unit: u };
    }
    return { num: str, unit: 'px' };
  };

  const { num: initNum, unit: initUnit } = parseValue(value);
  const [local, setLocal] = useState(initNum);
  const [unit,  setUnit]  = useState(initUnit);

  React.useEffect(() => {
    const { num, unit: u } = parseValue(value);
    setLocal(num);
    setUnit(u);
  }, [value]);

  const commit = (numVal, unitVal) => {
    const n = numVal.trim();
    if (n === '') { onChange(''); return; }
    const out = n + unitVal;
    if (out !== value) onChange(out);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', border: '1px solid #e4e9f0', borderRadius: '6px', overflow: 'hidden', background: '#f8f9fb', width: '100%', minWidth: 0, '&:focus-within': { borderColor: PRIMARY } }}>
      <input
        type="number"
        value={local}
        onChange={e => setLocal(e.target.value)}
        onBlur={() => commit(local, unit)}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
        placeholder={placeholder}
        style={{ flex: '1 1 0', minWidth: 0, width: 0, border: 'none', outline: 'none', padding: '4px 4px', fontSize: '12px', fontFamily: 'inherit', background: 'transparent', boxSizing: 'border-box' }}
      />
      <select
        value={unit}
        onChange={e => { setUnit(e.target.value); commit(local, e.target.value); }}
        style={{ border: 'none', borderLeft: '1px solid #e4e9f0', outline: 'none', padding: '0 4px', fontSize: '10px', fontFamily: 'inherit', background: '#f0f4f8', cursor: 'pointer', color: '#7a8494', flexShrink: 0, width: 32, appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', textAlign: 'center' }}
      >
        {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
      </select>
    </Box>
  );
}

// PxInput — thin wrapper over UnitInput for backwards compatibility.
export function PxInput({ value, onChange, placeholder, unit = 'px' }) {
  if (unit === '') {
    const [local, setLocal] = useState((value ?? '').toString());
    React.useEffect(() => { setLocal((value ?? '').toString()); }, [value]);
    const commit = () => { if (local !== (value ?? '').toString()) onChange(local); };
    return (
      <input type="number" value={local} onChange={e => setLocal(e.target.value)}
        onBlur={commit} onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
        placeholder={placeholder}
        style={{ width: '100%', border: '1px solid #e4e9f0', borderRadius: 6, padding: '4px 8px', fontSize: '12px', outline: 'none', fontFamily: 'inherit', background: '#f8f9fb', boxSizing: 'border-box' }}
        onFocus={e => e.target.style.borderColor = PRIMARY}
      />
    );
  }
  return <UnitInput value={value} onChange={onChange} placeholder={placeholder} />;
}

export function ColorInput({ value, onChange }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <input
        type="color"
        value={value && value.startsWith('#') ? value : '#000000'}
        onChange={e => onChange(e.target.value)}
        style={{ width: 28, height: 28, border: '1px solid #e4e9f0', borderRadius: 4, padding: 2, cursor: 'pointer', background: 'none' }}
      />
      <StyleInput value={value} onChange={onChange} placeholder="#000000 or rgba…" />
    </Box>
  );
}

export function PanelHeader({ icon, label, color, onClose, breakpoint }) {
  const BP_COLOR = { tablet: '#f59e0b', mobile: '#ef4444' };
  const bpLabel  = { tablet: 'Tablet', mobile: 'Mobile' };
  return (
    <Box sx={{ px: 2, py: 1.75, borderBottom: '1px solid #e8edf2', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 26, height: 26, borderRadius: 1.5, background: alpha(color, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>{icon}</Box>
        <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1a1a2e' }}>{label}</Typography>
        {breakpoint && breakpoint !== 'desktop' && (
          <Box sx={{ background: BP_COLOR[breakpoint], color: '#fff', fontSize: '0.6rem', fontWeight: 700, px: 0.75, py: 0.2, borderRadius: 1, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            {bpLabel[breakpoint]}
          </Box>
        )}
      </Box>
      {onClose && (
        <IconButton size="small" onClick={onClose} sx={{ color: '#9ea8b3', '&:hover': { color: '#1a1a2e' } }}>
          <CloseOutlined sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
}

export function VisibilityControl({ visibleOn = {}, onChange }) {
  const defaults = { desktop: true, tablet: true, mobile: true };
  const vis = { ...defaults, ...visibleOn };

  const BPS = [
    { key: 'desktop', icon: <DesktopWindowsOutlined sx={{ fontSize: 13 }} />, label: 'Desktop' },
    { key: 'tablet',  icon: <TabletOutlined         sx={{ fontSize: 13 }} />, label: 'Tablet'  },
    { key: 'mobile',  icon: <PhoneIphoneOutlined     sx={{ fontSize: 13 }} />, label: 'Mobile'  },
  ];

  const toggle = (key) => onChange({ ...vis, [key]: !vis[key] });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {BPS.map(({ key, icon, label }) => (
        <Box
          key={key}
          onClick={() => toggle(key)}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', py: 0.4, px: 0.5, borderRadius: 1, transition: 'background 0.1s', '&:hover': { background: '#f0f4f8' } }}
        >
          <Box sx={{ width: 16, height: 16, borderRadius: '4px', flexShrink: 0, border: `2px solid ${vis[key] ? PRIMARY : '#c0c8d4'}`, background: vis[key] ? PRIMARY : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.12s' }}>
            {vis[key] && (
              <Box sx={{ width: 8, height: 8, background: '#fff', borderRadius: '2px', transform: 'scale(0.8)' }}>
                <svg viewBox="0 0 8 8" style={{ width: '100%', height: '100%' }}>
                  <polyline points="1,4 3,6 7,2" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Box>
            )}
          </Box>
          <Box sx={{ color: vis[key] ? '#5a6474' : '#c0c8d4', display: 'flex', transition: 'color 0.12s' }}>{icon}</Box>
          <Typography sx={{ fontSize: '0.72rem', color: vis[key] ? '#3c4555' : '#c0c8d4', transition: 'color 0.12s', userSelect: 'none' }}>
            {label}
          </Typography>
          {!vis[key] && (
            <Typography sx={{ fontSize: '0.65rem', color: '#e67e22', fontWeight: 700, ml: 'auto' }}>Hidden</Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}